import { z } from 'zod';
import { apiEnvelope } from '../envelope';

// ─── HDHP cost-share notice derivation ───────────────────────────────────────
//
// Server-side derivation behind the member-authed hdhp-notice read. Clients
// never see plan logic, the program set, or the raw roster flag — only the
// derived boolean (the condition-agnostic-client rule). Every input that is
// absent or undetermined derives to `false`: a missing notice is recoverable
// on the cost-navigation call, a wrong notice erodes trust in every screen
// that follows.

/**
 * Program keys whose members carry cost-share exposure. Server-owned
 * configuration: the set mirrors the legacy cost-share program gate (the
 * medication-pathway substances — never tobacco). Keys are the
 * `program_definitions.key` vocabulary; a consuming test cross-checks them
 * against the program catalog so a renamed key fails loudly.
 */
export const COST_SHARE_PROGRAM_KEYS: readonly string[] = Object.freeze([
  'alcohol',
  'cannabis',
  'opioid',
]);

/**
 * Fail-closed derivation for the per-client cost-share capability: `true`
 * only when the company-config lookup returned a config whose
 * `is_cost_share` key is boolean `true`. A null lookup (company unknown
 * to the store), an absent key, a non-boolean value, and a failed lookup
 * (callers pass `null` on error — the read must never fail) all derive to
 * `false`: no cost warning is shown when the capability is undetermined.
 * Mirrors the minor-support accessor's posture over the same seam.
 */
export function deriveCostShareEnabled(
  config: { configs: Readonly<Record<string, unknown>> } | null,
): boolean {
  return config?.configs['is_cost_share'] === true;
}

export interface DeriveRequiresHdhpNoticeInput {
  /** Per-client cost-share capability, from `deriveCostShareEnabled`. */
  costShareEnabled: boolean;
  /** Member-level HDHP flag from the latest matched eligibility resolution, or null/undefined when unknown. */
  hdhpFlag: boolean | null | undefined;
  /** The member's ACTIVE main program key, or null when none is selected yet. */
  mainProgramKey: string | null | undefined;
}

/**
 * The one decision function behind `requiresHdhpNotice`: true exactly when
 * the client runs cost share, the member's roster record carries the HDHP
 * flag, and the selected main program sits in the cost-share set. Any absent
 * or undetermined input derives `false`; the function never throws.
 */
export function deriveRequiresHdhpNotice(input: DeriveRequiresHdhpNoticeInput): boolean {
  if (!input.costShareEnabled) return false;
  if (input.hdhpFlag !== true) return false;
  if (!input.mainProgramKey) return false;
  return COST_SHARE_PROGRAM_KEYS.includes(input.mainProgramKey);
}

// ─── wire contract ───────────────────────────────────────────────────────────

/** Constant-shape body for the member-authed hdhp-notice read (enumeration-safe). */
export const hdhpNoticeDataSchema = z
  .object({
    requiresHdhpNotice: z.boolean(),
  })
  .strict();
export type HdhpNoticeData = z.infer<typeof hdhpNoticeDataSchema>;

export const hdhpNoticeResponseSchema = apiEnvelope(hdhpNoticeDataSchema);
